import CustomerHelper from '@/helpers/customerHelper';
import { normalizeBaseUrl } from '@/helpers/tenantUrlHelper';
import { CrudService } from '@/services/crudService';

type CheckoutInfoPayload = {
  tenantUrl: string | null;
  saleIds: Array<number | string>;
  saleItems?: Array<Record<string, unknown>>;
  customerId?: number | string | null;
  customer?: Record<string, unknown> | null;
};

type CheckoutCustomerPayload = {
  tenantUrl: string | null;
  saleIds: Array<number | string> | null;
  saleItems?: Array<Record<string, unknown>> | null;
  paymentMethodId?: number | null;
  voucherId?: number | null;
  customerId?: number | string | null;
  customer?: Record<string, unknown> | null;
  tokenId?: string | number | null;
  tpvOnlineInfoTemp?: string | null;
  tpvOnlineNotPersitedInfo?: string | null;
  paymentFields?: Record<string, unknown> | null;
  promoCode?: string | null;
  origin?: number | null;
};

export async function fetchCheckoutInfo(options: CheckoutInfoPayload) {
  const tenantUrl = normalizeBaseUrl(options.tenantUrl);
  const baseUrl = tenantUrl || normalizeBaseUrl(process.env.VUE_APP_MEDKUS_BACKEND_URL);
  if (!baseUrl) {
    throw new Error('Tenant URL no disponible para checkout');
  }

  const useTenantUrl = tenantUrl !== '';
  const allowAuth = CustomerHelper.resolveCustomerId(options.customerId) !== null;
  const crud = new CrudService({
    baseUrl,
    allowAuth: true,
    handleUnauthorized: allowAuth && !useTenantUrl,
  });
  const customerId = !useTenantUrl ? CustomerHelper.resolveCustomerId(options.customerId) : null;
  const endpoint = useTenantUrl
    ? `${baseUrl}/api/shop/checkout-info-market`
    : (customerId !== null
      ? `${baseUrl}/api/shop/checkout-info-by-customer`
      : `${baseUrl}/api/shop/checkout-info`);

  const payload = useTenantUrl
    ? {
      saleIds: options.saleIds ?? [],
      saleItems: Array.isArray(options.saleItems) ? options.saleItems : [],
      customer: options.customer ?? null,
    }
    : {
      customerId,
      saleIds: options.saleIds ?? [],
      saleItems: Array.isArray(options.saleItems) ? options.saleItems : [],
    };

  const response = await crud.httpPost(endpoint, payload, undefined, 'json', {
    headers: {
      Accept: '*/*',
      'Content-Type': 'application/json',
    },
    baseUrl,
    allowAuth: true,
    handleUnauthorized: allowAuth && !useTenantUrl,
  });

  return response;
}

export async function completeCheckout(options: CheckoutCustomerPayload) {
  const tenantUrl = normalizeBaseUrl(options.tenantUrl);
  const baseUrl = tenantUrl || normalizeBaseUrl(process.env.VUE_APP_MEDKUS_BACKEND_URL);
  if (!baseUrl) {
    throw new Error('Tenant URL no disponible para finalizar checkout');
  }

  const useTenantUrl = tenantUrl !== '';
  const allowAuth = CustomerHelper.resolveCustomerId(options.customerId) !== null;
  const crud = new CrudService({
    baseUrl,
    allowAuth: true,
    handleUnauthorized: allowAuth && !useTenantUrl,
  });
  const customerId = !useTenantUrl ? CustomerHelper.resolveCustomerId(options.customerId) : null;
  const useCustomerEndpoint = customerId !== null;
  const endpoint = useTenantUrl
    ? `${baseUrl}/api/shop/checkout-market`
    : (useCustomerEndpoint
      ? `${baseUrl}/api/shop/checkout-customer`
      : `${baseUrl}/api/shop/checkout`);
  const customerPayload: Record<string, unknown> | null = options.customer
    ? { ...options.customer }
    : (customerId !== null ? { id: customerId } : null);

  if (customerPayload && (customerPayload as { id?: number | null }).id == null) {
    (customerPayload as { id?: number | null }).id = customerId;
  }

  const payload = useTenantUrl
    ? {
      saleIds: options.saleIds ?? [],
      saleItems: options.saleItems ?? [],
      paymentMethodId: options.paymentMethodId ?? null,
      voucherId: options.voucherId ?? null,
      customer: customerPayload,
      tokenId: options.tokenId ?? null,
      tpvOnlineInfoTemp: options.tpvOnlineInfoTemp ?? null,
      tpvOnlineNotPersitedInfo: options.tpvOnlineNotPersitedInfo ?? null,
      paymentFields: options.paymentFields ?? null,
      promoCode: options.promoCode ?? null,
      origin: options.origin ?? null,
    }
    : {
      saleIds: options.saleIds ?? [],
      saleItems: options.saleItems ?? [],
      paymentMethodId: options.paymentMethodId ?? null,
      voucherId: options.voucherId ?? null,
      customer: customerPayload,
      tokenId: options.tokenId ?? null,
      tpvOnlineInfoTemp: options.tpvOnlineInfoTemp ?? null,
      tpvOnlineNotPersitedInfo: options.tpvOnlineNotPersitedInfo ?? null,
      paymentFields: options.paymentFields ?? null,
      promoCode: options.promoCode ?? null,
      origin: options.origin ?? null,
    };

  const response = await crud.httpPost(endpoint, payload, undefined, 'json', {
    headers: {
      Accept: '*/*',
      'Content-Type': 'application/json',
    },
    baseUrl,
    allowAuth: true,
    handleUnauthorized: useCustomerEndpoint && !useTenantUrl,
  });

  return response;
}

type ValidatePromoPayload = {
  tenantUrl: string | null;
  code: string;
  saleIds: Array<number | string>;
  saleItems?: Array<Record<string, unknown>>;
  customerId?: number | string | null;
};

export async function validatePromo(options: ValidatePromoPayload) {
  const tenantUrl = normalizeBaseUrl(options.tenantUrl);
  const baseUrl = tenantUrl || normalizeBaseUrl(process.env.VUE_APP_MEDKUS_BACKEND_URL);
  if (!baseUrl) {
    throw new Error('Tenant URL no disponible para validar el cupón');
  }
  const useTenantUrl = tenantUrl !== '';
  const customerId = null;
  const allowAuth = !useTenantUrl;
  const crud = new CrudService({
    baseUrl,
    allowAuth,
    handleUnauthorized: allowAuth,
  });
  const payload = {
    code: options.code,
    customerId,
    saleItems: Array.isArray(options.saleItems) ? options.saleItems : [],
    saleIds: options.saleIds ?? [],
  };
  const response = await crud.httpPost(`${baseUrl}/api/promos/validate`, payload, undefined, 'json', {
    headers: {
      Accept: '*/*',
      'Content-Type': 'application/json',
    },
    baseUrl,
    allowAuth,
    handleUnauthorized: allowAuth,
  });
  return response;
}

export async function fetchStripeStatus(paymentIntentId: string | number, tenantUrl?: string | null) {
  const normalizedTenant = normalizeBaseUrl(tenantUrl);
  const baseUrl = normalizedTenant || normalizeBaseUrl(process.env.VUE_APP_MEDKUS_BACKEND_URL);
  if (!baseUrl) {
    throw new Error('Tenant URL no disponible para consultar el pago');
  }

  const safeId = String(paymentIntentId);
  const endpoint = `${baseUrl}/api/stripe/status/${safeId}`;

  const crud = new CrudService({
    baseUrl,
    allowAuth: true,
    handleUnauthorized: false,
  });

  const response = await crud.httpGet(endpoint, null, undefined, 'json', {
    headers: {
      Accept: '*/*',
    },
    baseUrl,
    allowAuth: true,
    handleUnauthorized: false,
  });

  return response;
}
